import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SupportingDocument from './SupportingDocument';
import ReviewTable from './ReviewTable';
import { apiService } from '../../../../common/apiCallService';
import PropTypes from 'prop-types';
import { BULKPAY } from '../../constant';
import { useJunoCommonFunctions } from '../../../helpers';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import JunoButton from '../../../../../new-ui/components/common/JunoButton';

const useFileUpload = ({ bulkPaymentId, apiUrl, onSuccess, setError, parentTransactionId }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const { saveAuditTrail } = useJunoCommonFunctions();
    const fileInputRef = useRef(null);

    const handleFileChange = event => {
        setError('');
        const files = Array.from(event.target.files);
        const oversized = files.filter(file => file.size > MAX_FILE_SIZE);

        if (oversized.length > 0) {
            setError('Some files exceed the 5MB size limit.');
        }

        const valid = files.filter(file => file.size <= MAX_FILE_SIZE);
        setSelectedFiles(prev => [...prev, ...valid]);
    };

    const handleRemoveFile = index => {
        setError('');
        const updated = selectedFiles.filter((_, i) => i !== index);
        setSelectedFiles(updated);

        if (fileInputRef.current) {
            const dt = new DataTransfer();
            updated.forEach(file => dt.items.add(file));
            fileInputRef.current.files = dt.files;
        }
    };

    const handleSubmit = async () => {
        if (!bulkPaymentId) {
            setError('Upload failed. Please try again.');
            return;
        }

        await apiService(
            `${
                window.location.hostname === 'localhost' ? apiUrl : ''
            }/restapi/bulk/update-status/${bulkPaymentId}`,
            {
                status: BULKPAY.STATUS.READY_FOR_PROCESSING,
                parentId: parentTransactionId,
            },
            () => {
                const activityData = JSON.parse(localStorage.getItem('activityData') || '');
                const userId = localStorage.getItem('user_name') || '';

                const auditTrailBody = {
                    userId: userId,
                    pageUrl: window.location.href,
                    eventType: 'Bulk Payment',
                    ipAddress: activityData?.ipAddress || '',
                    userAgent: window.navigator.userAgent,
                    newData: {
                        status: BULKPAY.STATUS.READY_FOR_PROCESSING,
                        parentId: parentTransactionId,
                        isWebUI: true,
                    },
                };

                saveAuditTrail(auditTrailBody);
            },
            err => setError(err || 'Upload failed. Please try again.'),
        );

        if (!selectedFiles.length) {
            onSuccess(selectedFiles);
            return;
        }

        setLoading(true);
        setError('');
        const formData = new FormData();
        formData.append('currentBulkPaymentId', bulkPaymentId);
        selectedFiles.forEach(f => formData.append('files', f));

        await apiService(
            `${
                window.location.hostname === 'localhost' ? apiUrl : ''
            }/restapi/bulk/upload-documents`,
            formData,
            () => {
                onSuccess(selectedFiles);
                setSelectedFiles([]);
                if (fileInputRef.current) fileInputRef.current.value = null;
            },
            err => setError(err || 'Upload failed. Please try again.'),
        );

        setLoading(false);
    };

    return {
        selectedFiles,
        loading,
        fileInputRef,
        handleFileChange,
        handleRemoveFile,
        handleSubmit,
    };
};

const Header = ({ imageBaseUrl, handleBack }) => (
    <div className="flex flex-row items-center gap-4 text-text">
        <ArrowBackIcon className="!hidden md:!block text-text" onClick={handleBack} />
        <p className="text-text uppercase font-inter text-xl font-semibold leading-loose tracking-tight">
            Review Payment
        </p>
    </div>
);

Header.propTypes = {
    imageBaseUrl: PropTypes.string.isRequired,
    handleBack: PropTypes.func.isRequired,
};

const InfoSection = ({ fileName }) => (
    <div>
        <p className="text-balanceCard-manage font-inter text-sm font-medium uppercase leading-tight">
            We have successfully imported payments from your file
            <span className="text-text font-inter text-sm uppercase font-semibold leading-tight ml-1">
                {fileName}
            </span>
            <br />
            <br />
            Upload relevant supporting documents and review the table below before submitting
            payment.
        </p>
    </div>
);

InfoSection.propTypes = {
    fileName: PropTypes.string,
};

const FileUploader = ({ fileInputRef, handleFileChange, error, imageBaseUrl, selectedFiles }) => (
    <div className="my-2 md:!my-8">
        <div className="flex flex-row items-center gap-2">
            <p className="text-balanceCard-manage leading-none uppercase font-inter text-xs font-medium mb-1.5 flex gap-1 items-center">
                Supporting Documents
                <img src={`${imageBaseUrl}/common/quetionmark.svg`} alt="Info" />
            </p>
        </div>
        <div className="flex items-center p-[.7rem] uppercase self-stretch rounded-lg  outline outline-1 outline-offset-[-1px] outline-input-border bg-[#fafafa] dark:bg-[#3F3F46]">
            <input
                type="file"
                multiple
                id="fileUpload"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                aria-label="Upload supporting documents"
            />
            <label
                htmlFor="fileUpload"
                data-e2e="choose-file-btn"
                className="cursor-pointer whitespace-nowrap min-w-min bg-[#E4E4E7] text-black leading-none uppercase text-xs p-[.65rem] rounded-md text-center">
                Choose File
            </label>

            <span className="ml-3 text-sm text-text truncate max-w-[200px] md:max-w-[300px] uppercase">
                {selectedFiles && selectedFiles.length > 0
                    ? selectedFiles.length === 1
                        ? selectedFiles[0].name 
                        : `${selectedFiles.length} files` 
                    : 'No file chosen'}
            </span>
        </div>
        {error && <div className="text-red-500 mt-2">{error}</div>}
    </div>
);

FileUploader.propTypes = {
    fileInputRef: PropTypes.object.isRequired,
    handleFileChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    imageBaseUrl: PropTypes.string.isRequired,
    selectedFiles: PropTypes.array,
};

// 🔹 Main Component
const ReviewPayment = ({
    handleContinue,
    handleBack,
    bulkPaymentId,
    parentTransactionId,
    imageBaseUrl,
    apiUrl,
}) => {
    const location = useLocation();
    const { fileName } = location.state || {};
    const [error, setError] = useState('');

    const {
        selectedFiles,
        loading,
        fileInputRef,
        handleFileChange,
        handleRemoveFile,
        handleSubmit,
    } = useFileUpload({
        bulkPaymentId,
        apiUrl,
        onSuccess: handleContinue,
        setError,
        parentTransactionId,
    });

    return (
        <>
            <div className="flex flex-col gap-3 w-full md:w-[560px] p-6 rounded-lg outline outline-1 outline-offset-[-1px] outline-bulkpay-card-outline bg-bulkpay-card-bg md:!mt-[60px] md:!mb-6 m-auto">
                <Header imageBaseUrl={imageBaseUrl} handleBack={handleBack} />
                <InfoSection fileName={fileName} />
                <FileUploader
                    fileInputRef={fileInputRef}
                    handleFileChange={handleFileChange}
                    error={error}
                    imageBaseUrl={imageBaseUrl}
                    selectedFiles={selectedFiles}
                />

                <JunoButton type="primary" disabled={loading} onClick={handleSubmit}>
                    Submit Bulk Payment
                </JunoButton>
            </div>

            {selectedFiles.length > 0 && (
                <SupportingDocument
                    selectedFiles={selectedFiles}
                    handleRemoveFile={handleRemoveFile}
                    imageBaseUrl={imageBaseUrl}
                />
            )}

            <ReviewTable transactionId={parentTransactionId} />
        </>
    );
};

ReviewPayment.propTypes = {
    handleContinue: PropTypes.func.isRequired,
    handleBack: PropTypes.func.isRequired,
    bulkPaymentId: PropTypes.string.isRequired,
    parentTransactionId: PropTypes.string.isRequired,
    imageBaseUrl: PropTypes.string.isRequired,
    apiUrl: PropTypes.string.isRequired,
};

export default ReviewPayment;
